export type RuleFunction<T> = (value: T) => true | string;

export type ValidateOnEvent = 'input' | 'blur';

export interface UseValidation<T> {
    rulesErrorMessage: Ref<string>;
    hasError: ComputedRef<boolean>;
    hasRules: ComputedRef<boolean>;
    computedError: ComputedRef<string>;
    runValidation: (value: T) => void;
    runValidationHandler: (value: T, event: ValidateOnEvent) => void;
    isValidateOn?: (value: ValidateOnEvent) => boolean;
}

// TODO: to improve with text input component to handle multiple errors?
export const useValidation = <T>(
    propErrorMessage: MaybeRefOrGetter<string>,
    rules: MaybeRefOrGetter<RuleFunction<T>[]> = ref([]),
    value: MaybeRefOrGetter<T>,
    validateOn?: MaybeRefOrGetter<ValidateOnEvent[]>
): UseValidation<T> => {
    const rulesErrorMessage = ref('');

    const computedError = computed(() => {
        return toValue(propErrorMessage) || rulesErrorMessage.value;
    });

    const hasError = computed(() => {
        return !!computedError.value;
    });

    const hasRules = computed(() => {
        const unrefedRules = toValue(rules);
        return !!(unrefedRules && unrefedRules.length > 0);
    });

    watch(
        () => toValue(rules),
        () => {
            runValidation(toValue(value));
        }
    );

    const runValidation = (value: T) => {
        if (!hasRules.value) {
            rulesErrorMessage.value = '';
            return;
        }
        let iterator = 0;
        const unrefedRules = toValue(rules);
        while (iterator < unrefedRules!.length) {
            const rule = unrefedRules![iterator];
            const ruleResult = rule(value);
            if (ruleResult === true) {
                rulesErrorMessage.value = '';
                iterator++;
            } else {
                rulesErrorMessage.value = ruleResult;
                return;
            }
        }
    };

    const runValidationHandler = (value: T, event: ValidateOnEvent) => {
        if (isValidateOn(event)) {
            runValidation(value);
        }
    };

    const isValidateOn = (event: ValidateOnEvent) => {
        const unrefedValidateOn = toValue(validateOn);
        return unrefedValidateOn ? unrefedValidateOn.includes(event) : true;
    };

    return {
        rulesErrorMessage,
        hasError,
        hasRules,
        computedError,
        runValidation,
        runValidationHandler,
        isValidateOn: validateOn ? isValidateOn : undefined,
    };
};
